'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp, Shield } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  screenName?: string;
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  retryCount: number;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    console.error(`[ErrorBoundary] ${this.props.screenName ?? 'Screen'} error:`, error, errorInfo);
  }

  handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: prev.retryCount + 1,
    }));
    this.props.onRetry?.();
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo, showDetails, retryCount } = this.state;
    const { screenName = 'Screen' } = this.props;

    const isApiError =
      error?.message?.toLowerCase().includes('fetch') ||
      error?.message?.toLowerCase().includes('network') ||
      error?.message?.toLowerCase().includes('api') ||
      error?.message?.toLowerCase().includes('supabase') ||
      error?.message?.toLowerCase().includes('failed');

    return (
      <div
        className="flex flex-col items-center justify-center min-h-[400px] p-8"
        role="alert"
        aria-live="assertive"
        aria-label={`Error in ${screenName}`}
      >
        {/* Glow backdrop */}
        <div className="relative w-full max-w-lg">
          <div className="absolute inset-0 bg-red-500/5 rounded-2xl blur-xl pointer-events-none" />

          <div className="relative bg-[#0d1117] border border-red-500/30 rounded-2xl p-8 shadow-[0_0_40px_rgba(239,68,68,0.08)]">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-3.5 h-3.5 text-red-400/70" aria-hidden="true" />
                  <span className="text-xs font-mono text-red-400/70 uppercase tracking-widest">
                    {isApiError ? 'API Failure' : 'Runtime Error'}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-white leading-tight">
                  {screenName} failed to load
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  {isApiError
                    ? 'A network or API error occurred while fetching data.'
                    : 'An unexpected error occurred in this component.'}
                </p>
              </div>
            </div>

            {/* Error message */}
            {error?.message && (
              <div className="mb-5 px-4 py-3 bg-red-950/30 border border-red-500/20 rounded-lg">
                <p className="text-sm font-mono text-red-300 break-words">{error.message}</p>
              </div>
            )}

            {/* Retry count badge */}
            {retryCount > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-xs text-gray-500 font-mono">
                  Retry attempts:{' '}
                  <span className="text-cyan-400">{retryCount}</span>
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/40 hover:border-cyan-400/60 text-cyan-300 hover:text-cyan-200 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                aria-label={`Retry loading ${screenName}`}
                tabIndex={0}
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Retry
              </button>

              <button
                onClick={this.toggleDetails}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 hover:border-gray-600/50 text-gray-400 hover:text-gray-300 rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500/50"
                aria-expanded={showDetails}
                aria-controls="error-details"
                aria-label="Toggle error details"
                tabIndex={0}
              >
                {showDetails ? (
                  <ChevronUp className="w-4 h-4" aria-hidden="true" />
                ) : (
                  <ChevronDown className="w-4 h-4" aria-hidden="true" />
                )}
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {/* Expandable stack trace */}
            {showDetails && (
              <div
                id="error-details"
                className="mt-2 p-4 bg-gray-950/80 border border-gray-800/60 rounded-lg overflow-auto max-h-48"
                role="region"
                aria-label="Error stack trace"
              >
                <p className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2">
                  Stack Trace
                </p>
                <pre className="text-xs font-mono text-gray-400 whitespace-pre-wrap break-words leading-relaxed">
                  {errorInfo?.componentStack ?? error?.stack ?? 'No stack trace available.'}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
